import { useEffect, useRef, useState } from 'react';
import Hls, { ErrorData } from 'hls.js';
import { isHLSSupported } from '../utilities/hlsSupport';
import { defaultHLSConfig } from '../utilities/hlsConfig';


interface HLSPlayerState {
  isLoading: boolean;
  isPlaying: boolean;
  error: string | null;
}

export function useHLSPlayer(videoUrl: string, keyPairId: string, policy: string, signature: string) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [state, setState] = useState<HLSPlayerState>({
    isLoading: false,
    isPlaying: false,
    error: null,
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) return;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    if (!isHLSSupported()) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'HLS is not supported in your browser'
      }));

      
      return;
    }

    const hls = new Hls({
        debug: false,
        enableWorker: true,
        lowLatencyMode: true,
        xhrSetup: (xhr, url) => {
          // Include CloudFront cookies
          // const cookies = document.cookie; // Get cookies from the document
          // xhr.withCredentials = true; // Allow credentials
          // xhr.setRequestHeader('Cookie', cookies); // Set the cookies in the request header

          // Check if the request is for a segment (not the playlist)
          if (url === videoUrl) {
            // This is the playlist URL, do not modify it
            xhr.open('GET', url, true);
          } else {
            // This is a segment URL, append CloudFront authentication parameters
            // const keyPairId = 'KA1I9KD5Q5I3B'; // Your Key-Pair-Id
            // const policy = 'eyJTdGF0ZW1lbnQiOlt7IlJlc291cmNlIjoiaHR0cHM6Ly92aWRlby5kZXYubWVkZm9yYWxsLmNvbS9tZmEtaHViLVJmNlNORy8qIiwiQ29uZGl0aW9uIjp7IkRhdGVMZXNzVGhhbiI6eyJBV1M6RXBvY2hUaW1lIjoxNzM0OTg0NzE4fX19XX0_'; // Your CloudFront policy
            // const signature = 'I8mIYuRRz5LqHma75gE7Cusc52RTLE7l2UGj3cHTlQRd1faURURVF1c8khQqx8v6DzBTr5cIlpSYClrTbKUc8BpTmHuQFpCdnY8fmYf64Pslt-2SOyABO-oAOe3x4Iez~KhWcq8xCGGE3zE3Zwrh4kU2KZ-MxsIhs577a1JO71HfDzKUnYIim0TtzatBSvoqfqwAK957tuJBHXgztQkIUuhmzl7MGLJxahQ8OWZpqCXlELscGzKxWX889zW0HXZNOo6WMLrtfVya-ff6IXIA9b4grMNQixyhJ2h1yjm3893v3I5CzyGK7f6Itv0jdEUpcGly3SyaJhnBMc26hdYQJA__'; // Your CloudFront signature

            const newUrl = `${url}?Key-Pair-Id=${keyPairId}&Policy=${encodeURIComponent(policy)}&Signature=${encodeURIComponent(signature)}`;
            xhr.open('GET', newUrl, true);
          }
        },
      });

    hlsRef.current = hls;

    const onError = (_: any, data: ErrorData) => {
      if (data.fatal) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: `Error loading stream: ${data.type}`
        }));
        hls.destroy();
      }
    };

    const onManifestParsed = () => {
      setState(prev => ({ ...prev, isLoading: false }));
      video.pause();
    };

    const onPlay = () => setState(prev => ({ ...prev, isPlaying: true }));
    const onPause = () => setState(prev => ({ ...prev, isPlaying: false }));

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);

    hls.on(Hls.Events.ERROR, onError);
    hls.on(Hls.Events.MANIFEST_PARSED, onManifestParsed);

    hls.loadSource(videoUrl);
    hls.attachMedia(video);

    video.addEventListener('loadedmetadata', () => {
      video.pause();
    });

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      hls.destroy();
      hlsRef.current = null;
      setState({
        isLoading: false,
        isPlaying: false,
        error: null,
      });
    };
  }, [videoUrl, keyPairId, policy, signature]);

  const controls = {
    play: async () => {
      if (videoRef.current) {
        try {
          await videoRef.current.play();
        } catch (error) {
          console.error('Error playing video:', error);
        }
      }
    },
    pause: () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    },
    setPlaybackSpeed: (speed: number) => {
      if (videoRef.current) {
        videoRef.current.playbackRate = speed;
      }
    },
    stepForward: () => {
      if (videoRef.current) {
        const frameTime = 1/30; // Assuming 30fps
        videoRef.current.currentTime = Math.min(
          videoRef.current.duration,
          videoRef.current.currentTime + frameTime
        );
      }
    },
    stepBackward: () => {
      if (videoRef.current) {
        const frameTime = 1/30; // Assuming 30fps
        videoRef.current.currentTime = Math.max(
          0,
          videoRef.current.currentTime - frameTime
        );
      }
    },
    jumpForward: () => {
      if (videoRef.current) {
        const jumpTime = 10/30; // 10 frames at 30fps
        videoRef.current.currentTime = Math.min(
          videoRef.current.duration,
          videoRef.current.currentTime + jumpTime
        );
      }
    },
    jumpBackward: () => {
      if (videoRef.current) {
        const jumpTime = 10/30; // 10 frames at 30fps
        videoRef.current.currentTime = Math.max(
          0,
          videoRef.current.currentTime - jumpTime
        );
      }
    },
  };

  return {
    videoRef,
    ...state,
    ...controls,
  };
}